import { defineMessages } from 'react-intl';

type MessageKeys =
	| 'reload'
	| 'traceIdMessage'
	| 'copyTraceId'
	| 'resend'
	| 'aupErrorMessagePartOne'
	| 'aupErrorMessagePartTwo'
	| 'aupLinkText'
	| 'networkErrorMessage'
	| 'clientNetworkErrorMessage'
	| 'productNotActiveErrorMessage'
	| 'authErrorMessage'
	| 'permissionErrorMessage'
	| 'serverErrorMessage'
	| 'chatStreamTimeoutError'
	| 'approvalRequiredForFirstPublishErrorMessage';

const message: Record<MessageKeys, { id: string; defaultMessage: string; description?: string }> =
	defineMessages({
		reload: {
			id: 'ai-mate.agent-message.error',
			defaultMessage: 'Reload chat',
			description: 'Button text for reloading the chat when an error occurs',
		},
		traceIdMessage: {
			id: 'ai-mate.agent-message.error.trace-id',
			defaultMessage: 'Trace ID: {traceId}',
			description: 'Message to display the trace ID when an error occurs',
		},
		copyTraceId: {
			id: 'ai-mate.agent-message.error.copy-trace-id',
			defaultMessage: 'Copy trace ID',
			description: 'Button text for copying the trace ID',
		},
		resend: {
			id: 'ai-mate.agent-message.error.resend',
			defaultMessage: 'Resend the message',
			description: 'Button text for resending the message',
		},
		aupErrorMessagePartOne: {
			id: 'ai-mate.agent-message.error.aup-one',
			defaultMessage: 'We can\u2019t respond to that prompt.',
			description: 'Message to display for AUP error p1',
		},
		aupErrorMessagePartTwo: {
			id: 'ai-mate.agent-message.error.aup-two',
			defaultMessage: 'Your prompt might not comply with our {link}. Try a different prompt.',
			description:
				'Error message text displayed in the second part of an Acceptable Use Policy (AUP) violation error within the AI conversation interface, explaining why the prompt was rejected and providing guidance. Shown as the second line of an error message. The link placeholder will be substituted with a clickable link to the Acceptable Use Policy. The purpose is to explain the policy violation and guide users to try a different prompt that complies with the policy.',
		},
		aupLinkText: {
			id: 'ai-mate.agent-message.error.aup-link-text',
			defaultMessage: 'Acceptable Use Policy',
			description:
				'Link text displayed on a hyperlink in an Acceptable Use Policy (AUP) error message within the AI conversation interface that opens the AUP documentation. Shown as clickable link text. When clicked, it opens the Acceptable Use Policy in a new tab or window. The purpose is to provide users with access to the policy so they can understand what content is allowed.',
		},
		networkErrorMessage: {
			id: 'ai-mate.agent-message.error.network-error',
			defaultMessage: 'There seems to be a network problem.',
			description:
				'User friendly message to display when there is a network error from stream generator',
		},
		clientNetworkErrorMessage: {
			id: 'ai-mate.agent-message.error.client-network-error',
			defaultMessage:
				'Unable to connect to Rovo. Please check your network connection or VPN and try again.',
			description:
				'User friendly message to display when the request never reached Atlassian servers due to a client-side network issue (e.g. VPN blocking, firewall, or no connectivity)',
		},
		productNotActiveErrorMessage: {
			id: 'ai-mate.agent-message.error.product-not-active',
			defaultMessage:
				'Rovo is not enabled for your organization. Please contact your Atlassian admin to enable Rovo.',
			description:
				'User friendly message to display when Rovo is not enabled for the user org (product-not-active error)',
		},
		authErrorMessage: {
			id: 'ai-mate.agent-message.error.auth-error',
			defaultMessage: 'Your session has expired. Please sign out and sign back in to continue.',
			description:
				'User friendly message to display when the user is not authenticated (401 error)',
		},
		permissionErrorMessage: {
			id: 'ai-mate.agent-message.error.permission-error',
			defaultMessage:
				"You don't have permission to use Rovo on this site. Please contact your Atlassian admin.",
			description:
				'User friendly message to display when the user does not have permission to use Rovo (403 error, e.g. IP allowlist, HIPAA restrictions)',
		},
		serverErrorMessage: {
			id: 'conversation-assistant.chat-history.store-error.serverErrorMessage',
			defaultMessage: 'Something went wrong. Please try again.',
			description:
				'User friendly message to display when Rovo returns a server error without exposing backend error details.',
		},
		chatStreamTimeoutError: {
			id: 'ai-mate.agent-message.error.stream-timeout',
			defaultMessage: 'The response took too long and timed out.',
			description:
				'User friendly message to display when Rovo Chat stops receiving stream events and times out the response.',
		},
		// The same message id, English string, and translator description
		// are intentionally duplicated in
		// `@atlassian/rovo-platform-ui-components/src/ui/confluence-object-finalize-popup/messages.ts`
		// so the Rovo Chat error surface and the finalize-popup surface
		// share one translation entry. The English text must be inlined
		// as a string literal here (rather than referenced from a shared
		// constant) because babel-plugin-formatjs requires every value
		// inside a `defineMessages({...})` call to be a string literal at
		// extraction time — identifier references crash the plugin. Keep
		// both copies of this message block in sync.
		// See CCIMPORTER-3221 / CCIMPORTER-3249.
		approvalRequiredForFirstPublishErrorMessage: {
			id: 'rovo-platform-ui-components.shared.error.approval-required-for-first-publish',
			defaultMessage:
				'This space requires approval before publishing new pages. Please submit the page for review.',
			description:
				'User-facing message shown when the user tries to publish a page (e.g. via Rovo Chat add-to-space or the Rovo finalize popup) to a Confluence space that requires approval before its first publish. The backend returns errorType=APPROVAL_REQUIRED_FOR_FIRST_PUBLISH on HTTP 409.',
		},
	});

export default message;
